import React, { useEffect, useRef, useState } from 'react';
import {
  CardNumberElement,
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import cx from 'classnames';
import { Formik } from 'formik';
import * as yup from 'yup';

import { ModalProvider, useModalContext } from '@magoosh/context/modal_context';
import { LinkButton } from '@magoosh/lib/button';
import { Form } from '@magoosh/lib/forms';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import { paths } from 'config/path_helpers';
import { bugsnagNotify } from 'utilities/bugsnag';
import { formatPrice } from 'utilities/money';

import * as api from '@payments/api';
import Confirmation from '@payments/components/confirmation';
import InstallmentsFormGroup from '@payments/components/form_groups/installments';
import PaymentMethodsFormGroup from '@payments/components/form_groups/payment_methods';
import UserFormGroup from '@payments/components/form_groups/user';
import CouponCodeInput from '@payments/components/inputs/coupon_code';
import ScrollToError from '@payments/components/scroll_to_error';
import SubmitPaymentButton from '@payments/components/submit_payment_button';
import EuTerms from '@payments/components/terms/eu';
import InstallmentsTerms from '@payments/components/terms/installments';
import UsTerms from '@payments/components/terms/us';
import { genericErrorMessage, PaymentError } from '@payments/errors';
import {
  BillingCycle,
  PaymentMethod,
  Pricing,
  PurchaseAgreementFormProps,
  PurchaseAgreementFormValues
} from '@payments/types';
import * as utils from '@payments/utils';

import style from '@payments/style.module.scss';

// The user's attributes are nested to match the UserForm nested in CreatePurchaseAgreementForm,
// so `signedIn` is no longer a sibling the credential validations can key off of. Build the
// schema from the prop instead.
const buildValidationSchema = (signedIn: boolean) => yup.object({
  cardName: yup
    .string()
    .label('Full Name')
    .when(['pricing', 'paymentMethod'], utils.shouldValidatePaymentInfo),
  termsAndPolicyAccepted: yup.bool().oneOf([true], 'Terms of Use and Privacy Policy must be accepted'),
  user: signedIn ? yup.object() : yup.object({
    email: yup.string().label('Email Address').required(),
    password: yup.string().label('Password').required(),
    passwordConfirmation: yup.string().label('Confirm Password').required()
  }),
  zipCode: yup
    .string()
    .label('Billing ZIP')
    .when(['pricing', 'paymentMethod'], utils.shouldValidatePaymentInfo)
});

interface WalletTerms {
  billingCycle: BillingCycle;
  pricing: Pricing;
}

// Reports what the browser wallet's sheet has to show from inside Formik, where the billing cycle
// and pricing live, out to the form that builds the sheet.
const WalletTermsObserver: React.FC<WalletTerms & { onChange: (terms: WalletTerms) => void }> = ({
  billingCycle,
  onChange,
  pricing
}) => {
  useEffect(() => {
    onChange({ billingCycle, pricing });
  }, [billingCycle, pricing]);

  return null;
};

const PurchaseAgreementFormGroups: React.FC<PurchaseAgreementFormProps> = (props) => {
  const authenticityToken = useAuthenticityToken();
  const { setModal } = useModalContext();
  // Formik automatically sets isSubmitting to false when our async handleSubmit finishes which
  // leads to the submit button being enabled for a bit before the user is redirected. To ensure
  // that the submit button stays disabled we manage our own state.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculatingPricing, setIsCalculatingPricing] = useState(false);
  const [cartAbandonQueued, setCartAbandonQueued] = useState(false);

  // Use refs to keep track of current couponCode and zipCode values. This lets us access to
  // current value to request new pricing. Without the ref we get stale values captured in a
  // closure from debounce.
  const couponCodeRef = useRef(props.purchaseAgreement.pricing.couponCode);
  const zipCodeRef = useRef(props.purchaseAgreement.pricing.zipCode || '');

  const elements = useElements();
  const stripe = useStripe();
  const subscriptionPlan = props.purchaseAgreement.subscriptionPlan;

  const initialBillingCycle = utils.planBillingCycle(subscriptionPlan);

  // Determine if Apple/Google Pay is available and set it up
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentRequestMethod, setPaymentRequestMethod] = useState<string>(null);
  // What the current sheet was built to charge today, so a purchase is never made from a sheet
  // that showed an older total
  const [paymentRequestCharge, setPaymentRequestCharge] = useState<number>(null);
  const [walletTerms, setWalletTerms] = useState<WalletTerms>({
    billingCycle: initialBillingCycle,
    pricing: props.purchaseAgreement.pricing
  });
  const walletChargeToday = utils.walletChargeToday(walletTerms.billingCycle, walletTerms.pricing);

  // Rebuilt whenever what the sheet shows changes, since Apple Pay only takes the charges to come
  // as the sheet is made. It has to be ready before the student clicks: Apple only opens a sheet
  // straight from a click.
  useEffect(() => {
    if (!stripe) return;

    let superseded = false;

    const checkPaymentRequestOptions = async () => {
      try {
        const pr: any = await api.createStripePaymentRequest(
          stripe,
          subscriptionPlan.name,
          walletChargeToday,
          utils.applePayRecurringPaymentRequest(
            subscriptionPlan,
            walletTerms.billingCycle,
            walletTerms.pricing,
            `${window.location.origin}${paths.profile()}`
          )
        );

        const result = await pr.canMakePayment();
        if (superseded || !result) return;

        setPaymentRequest(pr);
        setPaymentRequestCharge(walletChargeToday);

        if (result.applePay) {
          setPaymentRequestMethod('Apple Pay');
        } else if (result.googlePay) {
          setPaymentRequestMethod('Google Pay');
        }
      } catch(exception) {
        // Swallow the error since there is no negative user impact except that Google and Apple Pay
        // will not be available. But still log.
        bugsnagNotify(exception.debug);
      }
    };

    checkPaymentRequestOptions();

    return () => { superseded = true; };
  }, [stripe, walletTerms.billingCycle, walletChargeToday, walletTerms.pricing.renewalTotal]);

  const initialValues: PurchaseAgreementFormValues = {
    billingCycle: initialBillingCycle,
    cardName: '',
    couponCode: props.purchaseAgreement.pricing.couponCode,
    paymentMethod: PaymentMethod.STRIPE,
    pricing: props.purchaseAgreement.pricing,
    signedIn: props.signedIn,
    subscriptionPlanId: subscriptionPlan.id,
    termsAndPolicyAccepted: !props.isEuUser,
    user: {
      email: '',
      password: '',
      passwordConfirmation: ''
    },
    zipCode: props.purchaseAgreement.pricing.zipCode || ''
  };

  // A browser wallet hands Stripe its card before anything is bought, the same as a card typed in,
  // so the sheet comes first and the purchase is made from the card the student authorizes. The
  // sheet shows the total they are authorizing, so it has to be built from the pricing for the ZIP
  // they entered, tax included.
  const collectWalletPayment = (values: PurchaseAgreementFormValues, setErrors) => {
    const pricingIsCurrent = !isCalculatingPricing &&
      zipCodeRef.current === values.zipCode &&
      paymentRequestCharge === utils.walletChargeToday(values.billingCycle, values.pricing);

    if (!pricingIsCurrent) {
      setErrors({ _error: 'Your total is still updating. Please try again in a moment.' });
      setIsSubmitting(false);
      return;
    }

    // The sheet is theirs to cancel until they authorize, so the button goes back to being
    // clickable while it is open
    setIsSubmitting(false);
    paymentRequest.removeAllListeners();

    paymentRequest.on('paymentmethod', async (event) => {
      setIsSubmitting(true);
      let sheetOpen = true;

      try {
        const response = await api.submitPurchaseAgreement({
          ...values,
          authenticityToken,
          stripePaymentMethodId: event.paymentMethod.id,
          // The name entered is the only name checkout collects
          user: { ...values.user, name: values.cardName }
        });

        // Closed before any verification the bank asks for, which can't show over the sheet
        event.complete('success');
        sheetOpen = false;
        setModal(null);

        const nextUrl = response.nextUrl;
        if (response.actionRequired?.type === 'confirmPayment') {
          await api.confirmStripePayment(
            stripe,
            response.actionRequired.clientSecret,
            response.actionRequired.paymentMethodId,
            response.nextUrl,
            'card'
          );
        } else if (response.actionRequired) {
          throw new PaymentError(
            genericErrorMessage,
            `Unexpected action in collectWalletPayment with response ${JSON.stringify(response)}`
          );
        }

        if (nextUrl) {
          window.location.assign(nextUrl);
        } else {
          // Should not get here!
          throw new PaymentError(
            genericErrorMessage,
            `Unexpectedly reached the end of collectWalletPayment with response ${JSON.stringify(response)}`
          );
        }
      } catch(exception) {
        if (sheetOpen) event.complete('fail');
        setModal(null);
        setErrors(exception.errors);
        bugsnagNotify(`Payment Error: ${exception.debug || JSON.stringify(exception.errors)}`);
        // Nothing is going to navigate away from here, so the button is theirs to try again with
        setIsSubmitting(false);
      }
    });

    // For Google Pay we can directly launch into the payment request flow
    if (paymentRequestMethod === 'Google Pay') {
      paymentRequest.show();
    }
    // Apple needs a second click, since its sheet only opens straight from one and ours went
    // through validating the form first
    else {
      presentFinishPaymentRequestModal();
    }
  };

  const handleFreeOrder = async (values: PurchaseAgreementFormValues) => {
    const response = await api.createPurchaseAgreement(stripe, {
      ...values,
      authenticityToken,
      // Nothing is collected for a free purchase, so there is no payment method to report, and
      // with nothing to pay there are no installments either: it bills as the plan does. These
      // undefined keys are dropped from the request body by JSON.stringify.
      billingCycle: initialBillingCycle,
      paymentMethod: undefined,
      stripePaymentMethodId: undefined
    });

    if (response.nextUrl) {
      window.location.assign(response.nextUrl);
    } else {
      // Should not get here!
      throw new PaymentError(
        genericErrorMessage,
        `Unexpectedly reached the end of handleFreeOrder in PurchaseAgreementFormGroups with values ${JSON.stringify(values)}`
      );
    }
  };

  const handlePaidOrder = async (values: PurchaseAgreementFormValues, setErrors) => {
    if (values.paymentMethod === PaymentMethod.STRIPE_PAYMENT_REQUEST) {
      collectWalletPayment(values, setErrors);
      return;
    }

    let valuesForSubmit = values;

    if (utils.isCardPayment(values.paymentMethod) && !values.stripePaymentMethodId) {
      const card = elements.getElement(CardNumberElement);
      const { stripePaymentMethod } = await api.createStripePaymentMethod(stripe, card, values);
      valuesForSubmit = { ...valuesForSubmit, stripePaymentMethodId: stripePaymentMethod.id };
    } else if (values.paymentMethod === PaymentMethod.ALIPAY) {
      const { stripePaymentMethod } = await api.createStripePaymentMethod(stripe, null, values);
      valuesForSubmit = { ...valuesForSubmit, stripePaymentMethodId: stripePaymentMethod.id };
    }

    const response = await api.createPurchaseAgreement(
      stripe,
      {
        ...valuesForSubmit,
        authenticityToken,
        // The name on the card is the only name checkout collects
        user: { ...valuesForSubmit.user, name: valuesForSubmit.cardName }
      }
    );

    if (response.nextUrl) {
      window.location.assign(response.nextUrl);
    } else {
      // Should not get here!
      throw new PaymentError(
        genericErrorMessage,
        `Unexpectedly reached the end of handlePaidOrder in PurchaseAgreementFormGroups with values ${JSON.stringify(valuesForSubmit)}`
      );
    }
  };

  const handleOnSubmit = async (values: PurchaseAgreementFormValues, { setErrors }) => {
    setIsSubmitting(true);

    try {
      // Something with Stripe integration did not get set up correctly.
      if (utils.isCardPayment(values.paymentMethod) && (!stripe || !elements)) {
        throw new PaymentError(
          genericErrorMessage,
          '`stripe` or `elements` unexpectedly did not exist'
        );
      }

      // Free order, submit to Magoosh backend
      if (values.pricing.total <= 0) {
        await handleFreeOrder(values);
        return;
      } else {
        await handlePaidOrder(values, setErrors);
        return;
      }
    } catch(exception) {
      setErrors(exception.errors);
      bugsnagNotify(`Payment Error: ${exception.debug || JSON.stringify(exception.errors)}`);
      setIsSubmitting(false);
    }
  };

  const presentFinishPaymentRequestModal = () => {
    setModal({
      header: `Finish With ${paymentRequestMethod}`,
      body: (
        <div>
          <div>Click below to complete your payment for {subscriptionPlan.name}.</div>

          <PaymentRequestButtonElement
            className={cx(style.walletButton, 'u-margin-V-l')}
            options={{ paymentRequest }}
          />

          <LinkButton className="text-body" onClick={() => setModal(null)}>
            <small>&lt;&lt; Select a different payment method</small>
          </LinkButton>
        </div>
      )
    })
  }

  const updatePricing = (couponCode, paymentMethod, zipCode) => {
    return api.getPurchaseAgreementPricing(subscriptionPlan.id, couponCode, paymentMethod, zipCode)
  };

  const handleUserFormBlur = (values: PurchaseAgreementFormValues) => {
    if (cartAbandonQueued) return;
    if (values.pricing.total <= 0) return;

    const validateEmail = (email: string) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }

    if (values.user.email && validateEmail(values.user.email)) {
      api.queueCartAbandonFollowup(
        values.user.email,
        values.subscriptionPlanId,
        authenticityToken
      ).then((response) => {
        if (response.queued) {
          setCartAbandonQueued(true);
        }
      });
    }
  };

  // Matches the browser title PurchaseAgreementsController#new sets
  const heading = () => {
    if (props.purchaseAgreement.isUpgrade) return `Upgrade to ${subscriptionPlan.name}`;
    if (subscriptionPlan.priceCents > 0 && props.purchaseAgreement.pricing.total > 0) {
      return `Purchase ${subscriptionPlan.name}`;
    }
    return `Sign up for ${subscriptionPlan.name}`;
  };

  return (
    <>
      <div>
        <h1>{heading()}</h1>

        {subscriptionPlan.description && <div className="help-block">{subscriptionPlan.description}</div>}
      </div>

      <Formik
        initialValues={initialValues}
        onSubmit={handleOnSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={buildValidationSchema(props.signedIn)}>
        {({ values, handleSubmit, setFieldValue, errors }) => {
          const isPaidOrder = values.pricing.total > 0;
          const isInstallments = isPaidOrder && utils.isInstallments(values.billingCycle);
          const submitButtonName = isPaidOrder ? 'Complete Order' : 'Sign Up';

          const handleCouponCodeApplied = (couponCode) => {
            setIsCalculatingPricing(true);

            return updatePricing(couponCode, values.paymentMethod, zipCodeRef.current).then((newPricing) => {
              // If the response parrots back the code it was successfuly applied
              if (newPricing.couponCode?.toLowerCase() === couponCode?.toLowerCase()) {
                couponCodeRef.current = couponCode;
                setFieldValue('couponCode', couponCode);
                setFieldValue('pricing', newPricing);
              }

              setIsCalculatingPricing(false);
            });
          };

          const handlePaymentMethodChanged = (paymentMethod) => {
            // Every way to pay on checkout is taxed alike by its billing ZIP, so moving between
            // them keeps it. Anything that isn't clears it and its tax.
            if (!utils.collectsBillingDetails(paymentMethod)) {
              setIsCalculatingPricing(true);
              setFieldValue('zipCode', '');
              zipCodeRef.current = '';

              return updatePricing(couponCodeRef.current, paymentMethod, '').then((newPricing) => {
                setFieldValue('pricing', newPricing);
                setIsCalculatingPricing(false);
              });
            }
          };

          const handleZipCodeChanged = (zipCode) => {
            zipCodeRef.current = zipCode;
            setIsCalculatingPricing(true);

            return updatePricing(couponCodeRef.current, values.paymentMethod, zipCode).then((newPricing) => {
              setFieldValue('pricing', newPricing);
              setIsCalculatingPricing(false);
            });
          };

          return (
            <Form className={style.formikForm}>
              <ScrollToError />
              <WalletTermsObserver
                billingCycle={values.billingCycle}
                onChange={setWalletTerms}
                pricing={values.pricing}
              />
              {!props.signedIn && <UserFormGroup handleBlur={()=> handleUserFormBlur(values)} /> }

              {isPaidOrder && (
                <>
                  {props.purchaseAgreement.allowInstallments && (
                    <InstallmentsFormGroup
                      billingCycle={values.billingCycle}
                      onChange={(billingCycle) => {
                        setFieldValue('billingCycle', billingCycle);
                        // Installments are paid by a card or wallet Stripe can bill again, which
                        // is kept when paying in full too. Anything else starts back at card.
                        if (!utils.billsCardOnFile(values.paymentMethod)) {
                          setFieldValue('paymentMethod', PaymentMethod.STRIPE);
                        }
                      }}
                      subscriptionPlan={subscriptionPlan}
                      totalExcludingTax={values.pricing.totalExcludingTax}
                    />
                  )}

                  <PaymentMethodsFormGroup
                    allowedPaymentMethods={
                      subscriptionPlan.recurring ?
                        [PaymentMethod.STRIPE, PaymentMethod.STRIPE_PAYMENT_REQUEST] :
                        undefined
                    }
                    billingCycle={values.billingCycle}
                    onZipCodeChanged={handleZipCodeChanged}
                    onPaymentMethodChanged={handlePaymentMethodChanged}
                    paymentMethod={values.paymentMethod}
                    paymentRequestMethod={paymentRequestMethod}
                    totalExcludingTax={values.pricing.totalExcludingTax}
                  />
                </>
              )}

              {subscriptionPlan.priceCents > 0 && (
                <fieldset className="inputs">
                  <div className="form-group">
                    <legend>
                      <span>Confirmation</span>
                    </legend>

                    <Confirmation
                      isCalculatingPricing={isCalculatingPricing}
                      isInstallments={isInstallments}
                      listTotal={values.pricing.listTotal}
                      pricingComponents={values.pricing.components}
                      renewalTax={values.pricing.renewalTax}
                      renewalTotal={values.pricing.renewalTotal}
                      replacedAgreement={props.purchaseAgreement.replacedAgreement}
                      subscriptionPlan={subscriptionPlan}
                    />
                  </div>

                  <div className="u-margin-T-xs">
                    <CouponCodeInput
                      couponCode={values.pricing.couponCode}
                      onApply={handleCouponCodeApplied}
                    />
                  </div>
                </fieldset>
              )}

              {props.isEuUser && <EuTerms buttonName={submitButtonName} />}

              <div className="form-group u-margin-T-s">
                <SubmitPaymentButton
                  isPaidOrder={isPaidOrder}
                  isSubmitting={isSubmitting}
                  onClick={handleSubmit}
                  paymentMethod={values.paymentMethod}
                  paymentRequestMethod={paymentRequestMethod}
                />
              </div>

              {
                isInstallments ? (
                  <InstallmentsTerms
                    buttonName={submitButtonName}
                    installmentInterval={subscriptionPlan.installmentInterval}
                    installmentIntervalCount={subscriptionPlan.installmentIntervalCount}
                    installmentPrice={utils.calculateInstallmentsPrice(values.pricing.total)}
                    isEuUser={props.isEuUser}
                  />
                ) : (
                  !props.isEuUser && <UsTerms buttonName={submitButtonName} />
                )
              }
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export const RegistrationForm: React.FC<PurchaseAgreementFormProps> = (props) => {
  const StripeElementsWrapper = ({ children }) => {
    const stripePromise = loadStripe(props.purchaseAgreement.stripeToken);
    return <Elements stripe={stripePromise}>{children}</Elements>;
  };

  return (
    <StripeElementsWrapper>
      <ModalProvider>
        <PurchaseAgreementFormGroups {...props} />
      </ModalProvider>
    </StripeElementsWrapper>
  );
};
RegistrationForm
export default RegistrationForm;
